/**
 * Parse a newline-delimited JSON response as it arrives.
 *
 * The perfusion dashboard route sends one JSON object per line and flushes each
 * as it is built, so the point of reading it this way is that the caller can act
 * on a section while the rest are still being computed server-side. Buffering
 * the whole body and parsing it at the end would give the same data and undo
 * the reason the route streams.
 *
 * A chunk boundary can fall anywhere, including mid-character in a multi-byte
 * sequence, so decoding is incremental (`{ stream: true }`) and a partial line
 * is held until its newline arrives. A trailing line with no newline is still
 * yielded, so a server that ends without one loses nothing.
 *
 * Blank lines are skipped. A line that will not parse is skipped too, with the
 * reason: one malformed section should cost that section, not the response.
 */
export async function* ndjsonLines<T = unknown>(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<T> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffered += decoder.decode(value, { stream: true });

      let newline = buffered.indexOf('\n');
      while (newline !== -1) {
        const line = buffered.slice(0, newline);
        buffered = buffered.slice(newline + 1);
        const parsed = parseLine<T>(line);
        if (parsed !== undefined) {
          yield parsed;
        }
        newline = buffered.indexOf('\n');
      }
    }

    buffered += decoder.decode();
    const last = parseLine<T>(buffered);
    if (last !== undefined) {
      yield last;
    }
  } finally {
    /*
      Cancel before releasing, so a caller that stops early stops the download too.

      `releaseLock` only unlocks the body; it is not a signal to the underlying source, so the
      browser keeps pulling the rest of the response. `loadPerfusionDashboard` leaves this loop
      early on every case change, and the dashboard route is the largest response the app reads,
      so what that left behind was the whole of the abandoned case's stream still arriving.

      It runs on the normal path too, where the stream is already closed and `cancel` is a no-op.
      A stream torn down by an aborted `fetch` rejects instead, and that rejection is nothing to
      act on — the connection it would have closed is gone.
    */
    try {
      await reader.cancel();
    } catch {
      // Already closed, cancelled or errored. Either way there is nothing left to release it from.
    }
    reader.releaseLock();
  }
}

const parseLine = <T>(line: string): T | undefined => {
  const trimmed = line.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    return JSON.parse(trimmed) as T;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[ndjson] line is not valid JSON', { line: trimmed, error });
    return undefined;
  }
};
